/** Tamper-detection detail block. Only renders when the verify endpoint
 * reports `broken != null`. Shows the exact event sequence and the
 * recomputed-vs-stored hash mismatch so the auditor can pinpoint the
 * compromised row. */
import { useTranslation } from "react-i18next";

import type { VerifyResponse } from "./types";


type Props = {
  broken: NonNullable<VerifyResponse["broken"]>;
};


export function TamperDetail({ broken }: Props) {
  const { t } = useTranslation();
  return (
    <section className="card space-y-2 ring-1 ring-cvp-danger/40">
      <header className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-cvp-danger">
          {t("ui.tamperDetail.heading")}
        </h2>
        <span className="text-xs text-nb-text-2">
          {t("ui.tamperDetail.sequence", { sequence: broken.sequence })}
        </span>
      </header>
      <p className="text-xs text-nb-text">
        {t("ui.tamperDetail.body", { sequence: broken.sequence })}
      </p>
      <dl className="space-y-1 text-xs">
        <HashPair label={t("ui.tamperDetail.expectedHash")} value={broken.expected_hash} />
        <HashPair label={t("ui.tamperDetail.storedHash")} value={broken.stored_hash} />
        {broken.expected_prev_hash && (
          <HashPair label={t("ui.tamperDetail.expectedPrev")} value={broken.expected_prev_hash} />
        )}
        {broken.stored_prev_hash && (
          <HashPair label={t("ui.tamperDetail.storedPrev")} value={broken.stored_prev_hash} />
        )}
      </dl>
    </section>
  );
}


function HashPair({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <dt className="w-32 shrink-0 text-nb-text-3">{label}</dt>
      <dd className="break-all font-mono text-nb-text">{value}</dd>
    </div>
  );
}
